import { useState } from 'react'
import { Box, Button, Grid, IconButton, Link, Typography } from '@mui/material'
import StatusAnimated from '../../../../../../../components/StatusAnimated'
import moment from 'moment'
import DisplayContadores from '../../../../../../../components/DisplayContadores'
import ChipWithTooltip from '../../../../../../../components/ChipWithTooltip'
import { IDetalhesFechamentos, TFiltrosDetalheFechamento } from '../../../../../../../types/api/financeiro/detalhar-fechamentos-conta-corrente'
import { formatarValorMonetario } from '../../../../../../../helpers/masks'
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DialogImprimirFaturas from "./DialogImprimirFaturas";
import ConfiguracoesCabecalho, { TConfiguracoesCabecalho } from './ConfiguracoesCabecalho'
import DialogFinalizarFechamento from './DialogFinalizarFechamento'
import DialogDetalheTotal from './DialogDetalheTotal'
import DialogExcluirFechamento from './DialogExcluirFechamento'
import DialogCancelarFechamento from './DialogCancelarFechamento'
import { usePermission } from '../../../../../../../components/PermissionGuard'

export default function CabecalhoFechamento({
    id,
    isEdit,
    data,
    filtrosParams,
    onClose,
    handleUpdateCabecalho
}: CabecalhoFechamentoProps) {

    const realizarFechamento = usePermission('financeiro.cco-realizar-fechamento')
    const cancelarFechamento = usePermission('financeiro.cco-cancelar-fechamento')

    const [openDetalheTotal, setOpenDetalheTotal] = useState<"débitos" | "créditos" | null>(null)
    const [openModal, setOpenModal] = useState<TModaisCabecalho>({
        cancelarFechamento: false,
        finalizarFechamento: false,
        excluirFechamento: false,
        configuracoes: false,
        imprimirFaturas: false
    })


    const handleChangeModalState = (prop: keyof TModaisCabecalho, state: boolean) => {
        setOpenModal((prevState) => ({
            ...prevState,
            [prop]: state,
        }));
    }

    return (
        <>
            {
                !isEdit &&
                <Grid container spacing={2} mb={2}>
                    <Grid size={{xs: 12}}>
                        <Box display={'flex'} justifyContent={'space-between'}>
                            <Box>
                                <Box display={'flex'} gap={2} alignItems={'baseline'}>
                                    <Typography variant="h6" mb={0}><b>{data.nome}</b></Typography>
                                    <StatusAnimated status={data.status} size={15} />
                                </Box>
                                {
                                    data.status === 'Fechado' &&
                                    <Typography variant="subtitle2" sx={{ opacity: 0.6 }}>
                                        Fechado em {moment(data.data_fechamento).format('DD/MM/YYYY')}
                                    </Typography>
                                }
                                {
                                    data.status === 'Cancelado' &&
                                    <Typography variant="subtitle2" color={'error'}>
                                        Cancelada em {moment(data.cancelado_em).format('DD/MM/YYYY')} por {data.cancelado_por}
                                    </Typography>
                                }
                                {
                                    cancelarFechamento &&
                                    data.status === 'Fechado' &&
                                    <Link
                                        variant="subtitle2"
                                        color="error"
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => handleChangeModalState('cancelarFechamento', true)}>
                                        Cancelar
                                    </Link>
                                }
                            </Box>
                            {
                                data.status === 'Fechado' &&
                                <Box>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleChangeModalState('imprimirFaturas', true)}
                                        startIcon={<PrintIcon fontSize="small" />}
                                    >
                                        Imprimir faturas
                                    </Button>
                                </Box>
                            }
                        </Box>
                    </Grid>
                </Grid>
            }
            {
                isEdit &&
                <>
                    <Grid container spacing={2} mb={2}>
                        <Grid size={{xs: 12, md: 6}}>
                            <Box display={'flex'} gap={2}>
                                <Box display={'flex'} alignItems={'baseline'} gap={1}>
                                    <Typography variant="h6" mb={0}><b>{data.nome}</b></Typography>
                                    <StatusAnimated status={data.status} size={15} />
                                </Box>
                                <IconButton size="small" aria-label="delete" onClick={() => handleChangeModalState('configuracoes', true)}>
                                    <EditIcon fontSize="inherit" sx={{ color: "black" }} />
                                </IconButton>
                            </Box>

                            <Typography
                                mb={2}
                                variant="subtitle2"
                                sx={{ opacity: 0.6 }}
                            >
                                Fechamento em {moment(data.data_fechamento).format('DD/MM/YYYY')}
                            </Typography>

                            <Typography variant="subtitle2">
                                <b>Vencimento dos títulos a serem gerados:</b>
                            </Typography>

                            <DisplayContadores
                                titulo="Títulos a pagar"
                                valor={`${data.vencimento_titulos_pagar.length > 0 ? moment(data.vencimento_titulos_pagar[0]).format('DD/MM/YYYY') : '-'}`}
                                chip={
                                    data.vencimento_titulos_pagar.length > 1 ?
                                        <ChipWithTooltip
                                            quebraLinha
                                            extraLabels={data.vencimento_titulos_pagar.slice(1).map(date => moment(date).format('DD/MM/YYYY'))} label={`+${data.vencimento_titulos_pagar.length - 1}`}
                                        /> : null
                                }
                            />

                            <DisplayContadores
                                titulo="Títulos a receber"
                                valor={`${data.vencimento_titulos_receber.length > 0 ? moment(data.vencimento_titulos_receber[0]).format('DD/MM/YYYY') : '-'}`}
                                chip={
                                    data.vencimento_titulos_receber.length > 1 ?
                                        <ChipWithTooltip
                                            quebraLinha
                                            extraLabels={data.vencimento_titulos_receber.slice(1).map(date => moment(date).format('DD/MM/YYYY'))} label={`+${data.vencimento_titulos_receber.length - 1}`}
                                        /> : null}
                            />

                            {
                                realizarFechamento &&
                                <Link
                                    variant="subtitle2"
                                    color="primary"
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => handleChangeModalState('finalizarFechamento', true)}>
                                    Fechar agora?
                                </Link>
                            }
                        </Grid>
                        <Grid size={{xs: 12, md: 6}}>
                            <Box display="flex" flexDirection={'column'} gap={2} ml={4}>
                                <Box
                                    sx={{
                                        backgroundColor: '#f0fff4',
                                        borderRadius: 2,
                                        padding: 2,
                                        width: '100%'
                                    }}
                                >
                                    <Box display={'flex'} justifyContent={'space-between'}>
                                        <Typography variant="subtitle2" color="#1B5E20">
                                            Créditos
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            aria-label="delete"
                                            onClick={() => {
                                                setOpenDetalheTotal('créditos')
                                            }}
                                        >
                                            <VisibilityIcon
                                                fontSize="inherit"
                                                sx={{ color: 'black' }}
                                            />
                                        </IconButton>
                                    </Box>
                                    <Typography variant="h6" color="#1B5E20">
                                        {formatarValorMonetario(data.total_creditos || 0)}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        backgroundColor: '#fff5f5',
                                        borderRadius: 2,
                                        padding: 2,
                                        width: '100%'
                                    }}
                                >
                                    <Box display={'flex'} justifyContent={'space-between'}>
                                        <Typography variant="subtitle2" color="#960000">
                                            Débitos
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            aria-label="delete"
                                            onClick={() => {
                                                setOpenDetalheTotal('débitos')
                                            }}
                                        >
                                            <VisibilityIcon
                                                fontSize="inherit"
                                                sx={{ color: 'black' }}
                                            />
                                        </IconButton>
                                    </Box>

                                    <Typography variant="h6" color="#960000">
                                        {formatarValorMonetario(data.total_debitos || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </>
            }
            {
                openModal.configuracoes &&
                <ConfiguracoesCabecalho
                    open={openModal.configuracoes}
                    filtrosParams={filtrosParams}
                    handleUpdateFiltros={(value: TConfiguracoesCabecalho) => {
                        handleUpdateCabecalho(value);
                        handleChangeModalState('configuracoes', false)
                    }}
                    onClose={() => handleChangeModalState('configuracoes', false)}
                    handleExcluir={() => handleChangeModalState('excluirFechamento', true)}
                />
            }
            {
                openModal.finalizarFechamento &&
                <DialogFinalizarFechamento
                    id={id}
                    open={openModal.finalizarFechamento}
                    onClose={() => handleChangeModalState('finalizarFechamento', false)}
                    handleFinalizado={() => {
                        handleChangeModalState('finalizarFechamento', false);
                        onClose();
                    }}
                />
            }
            {
                openDetalheTotal &&
                <DialogDetalheTotal
                    open={Boolean(openDetalheTotal)}
                    handleClose={() => setOpenDetalheTotal(null)}
                    label={openDetalheTotal}
                    total={data.detalhes_total}
                />
            }
            {
                openModal.excluirFechamento &&
                <DialogExcluirFechamento
                    id={id}
                    open={openModal.excluirFechamento}
                    onClose={() => handleChangeModalState('excluirFechamento', false)}
                    handleExcluido={() => {
                        handleChangeModalState('excluirFechamento', false);
                        onClose();
                    }}
                />
            }
            {
                openModal.cancelarFechamento &&
                <DialogCancelarFechamento
                    id={id}
                    open={openModal.cancelarFechamento}
                    onClose={() => handleChangeModalState('cancelarFechamento', false)}
                    handleExcluido={() => {
                        handleChangeModalState('cancelarFechamento', false);
                        onClose();
                    }}
                />
            }{
                openModal.imprimirFaturas &&
                <DialogImprimirFaturas
                    id={id}
                    open={openModal.imprimirFaturas}
                    onClose={() => handleChangeModalState('imprimirFaturas', false)}
                    handleImprimir={() => {
                        handleChangeModalState('imprimirFaturas', false);
                        onClose();
                    }}
                />
            }
        </>
    )
}

interface CabecalhoFechamentoProps {
    id: number
    isEdit: boolean
    data: IDetalhesFechamentos
    filtrosParams: TFiltrosDetalheFechamento
    onClose: () => void;
    handleUpdateCabecalho: (value: TConfiguracoesCabecalho) => void
}

type TModaisCabecalho = {
    cancelarFechamento: boolean
    finalizarFechamento: boolean
    excluirFechamento: boolean
    configuracoes: boolean
    imprimirFaturas: boolean
}